import React, { useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'

import type { AdContainerProps, AdEvent } from './types'
import { Sponsorship } from '../../../../../shared/components/Sponsorship/Sponsorship'
import { buildVideoAdTagUrl } from '../utils'
import { useAppSelector } from '../../../hooks/useStore'
import { useVideoPlayerData } from '../VideoPlayerContext'
import {
  ComcastErrorType,
  triggerSentryError,
} from '../../../../../shared/helpers'

const AdContainer = (props: AdContainerProps) => {
  const sponsorshipRef = useRef<Sponsorship>(null)
  const videoDetails = useAppSelector((state) => state.content.videoDetails)
  const stationCallSign = useAppSelector((state) => state.station.call_sign)
  const [isAdPaused, setIsAdPaused] = useState(false)
  const { userPid, hasValidPassport } = useAppSelector((state) => ({
    hasValidPassport: state.user.validPassport,
    userPid: state.user.pid,
  }))
  const { latEnabled, deviceModel } = useAppSelector((state) => ({
    latEnabled: state.device.latEnabled,
    deviceModel: state.device.deviceModel,
  }))
  const {
    playerState,
    setAdData,
    setDuration,
    setPlayerStatus,
    setAdCurrentTime,
  } = useVideoPlayerData()
  const adTagUrl = useMemo(
    () =>
      buildVideoAdTagUrl({
        contentScope: videoDetails.contentScope,
        callSign: stationCallSign,
        legacyTpMediaId: videoDetails.legacyTpMediaId,
        ppid: userPid,
        latEnabled: latEnabled,
        deviceModel: deviceModel,
        validPassport: hasValidPassport,
      }),
    [
      videoDetails.contentScope,
      stationCallSign,
      videoDetails.legacyTpMediaId,
      userPid,
      latEnabled,
      deviceModel,
      hasValidPassport,
    ]
  )
  const isAdPlaying =
    playerState.status === 'adPlaying' || playerState.status === 'adPaused'
  const timeRemaining = Math.max(
    0,
    Math.floor(playerState.duration - (playerState.adData?.adCurrentTime ?? 0))
  )
  const countdownClassNames = cx('sponsorship-countdown', {
    'sponsorship-countdown--visible': isAdPlaying,
  })

  useEffect(() => {
    if (playerState.status === 'adPaused') {
      sponsorshipRef.current?.adsManager.pause()
      setIsAdPaused(true)
    } else if (playerState.status === 'adPlaying' && isAdPaused) {
      sponsorshipRef.current?.adsManager.resume()
      setIsAdPaused(false)
    }
  }, [playerState.status])

  const onContentPauseRequested = () => {
    props.pause()
    props.emitMuxEvent('adbreakstart')
    props.emitMuxEvent('adplay')
  }

  const onContentResumeRequested = () => {
    props.backToPlayingContent()
  }

  const onAdLoaded = (adEvent: AdEvent): void => {
    const adData = adEvent.getAdData()
    setAdData({
      adDuration: adData.duration,
      adMediaUrl: adData.mediaUrl,
      sourceHeight: adData.vastMediaHeight,
      sourceWidth: adData.vastMediaWidth,
      adCurrentTime: 0,
    })
    setDuration(adData.duration)
    props.emitMuxEvent('adresponse', {
      ad_tag_url: adTagUrl,
      ad_asset_url: adData.mediaUrl,
    })
  }

  const onAdPaused = () => {
    setPlayerStatus('adPaused')
    props.emitMuxEvent('adpause')
  }

  const onAdResumed = () => {
    props.emitMuxEvent('adresume', {
      ad_asset_url: playerState.adData?.adMediaUrl,
    })
  }
  const onAdStarted = () => {
    setPlayerStatus('adPlaying')
    props.emitMuxEvent('adplaying', {
      ad_asset_url: playerState.adData?.adMediaUrl,
    })
  }

  const onAdFirstQuartile = () => {
    props.emitMuxEvent('adfirstquartile')
  }
  const onAdMidPoint = () => {
    props.emitMuxEvent('admidpoint')
  }
  const onAdThirdQuartile = () => {
    props.emitMuxEvent('adthirdquartile')
  }

  const onAdProgress = (adEvent) => {
    const adData = adEvent.getAdData()
    setAdCurrentTime(adData.currentTime)
  }

  const onAdCompleted = () => {
    props.emitMuxEvent('adended', {
      ad_asset_url: playerState.adData?.adMediaUrl,
    })
  }

  const onInitializeIMAError = (error) => {
    props.backToPlayingContent()
    props.emitMuxEvent('aderror')

    triggerSentryError('Error initializing IMA', 'AdContainer.tsx', {
      errorName: error.name ?? 'An error occurred (default)',
      errorCode: error.code ?? 'Undefined error code (default)',
      errorMessage: error.message ?? 'Undefined error message (default)',
    })
  }

  const onAdError = (adError) => {
    props.backToPlayingContent()
    props.emitMuxEvent('aderror')
    triggerSentryError('onAdError', 'AdContainer.tsx', {
      error: adError.error?.data ?? 'No error information (default message)',
      adError: adError.getError,
      errorLocation: 'AdContainer - onAdError',
      errorType: ComcastErrorType.media,
    })
  }

  const onAdsManagerLoadedError = (adError) => {
    props.emitMuxEvent('aderror')
    props.backToPlayingContent()
    triggerSentryError('Error onAdsManagerLoadedError', 'VideoPlayer.js', {
      error: adError.error?.data ?? 'No error information (default message)',
    })
  }

  return (
    <>
      <Sponsorship
        ref={sponsorshipRef}
        playerRef={props.playerRef}
        adTagUrl={adTagUrl}
        onContentPauseRequested={onContentPauseRequested}
        onContentResumeRequested={onContentResumeRequested}
        onAdLoaded={onAdLoaded}
        onAdPaused={onAdPaused}
        onAdResume={onAdResumed}
        onAdStarted={onAdStarted}
        onAdFirstQuartile={onAdFirstQuartile}
        onAdMidPoint={onAdMidPoint}
        onAdThirdQuartile={onAdThirdQuartile}
        onAdProgress={onAdProgress}
        onAdCompleted={onAdCompleted}
        onAllAdsCompleted={onAdCompleted}
        onAdError={onAdError}
        onAdsManagerLoadedError={onAdsManagerLoadedError}
        onInitializeIMAError={onInitializeIMAError}
      />
      <div className={countdownClassNames}>{`Message (${timeRemaining})`}</div>
    </>
  )
}

export default AdContainer
